// Logique de combat, status effects, compétences et buffs de combat
import { ENERGY_INIT, ENERGY_MAX } from "../data/classes";

// ============ SYSTÈME ÉLÉMENTAIRE ============
// Cycle : Eau > Feu > Terre > Air > Eau (chaque élément bat le suivant)
// Lumière ↔ Dark : forts l'un contre l'autre
// Lumière/Dark neutres vs F/E/T/A
// Même élément = neutre (×1)
export const ELEMENTS = {
  feu: { nom: "Feu", icon: "🔥", couleur: "bg-rose-500", textColor: "text-white" },
  eau: { nom: "Eau", icon: "💧", couleur: "bg-cyan-500", textColor: "text-white" },
  terre: { nom: "Terre", icon: "🌿", couleur: "bg-amber-700", textColor: "text-white" },
  air: { nom: "Air", icon: "💨", couleur: "bg-sky-300", textColor: "text-slate-950" },
  lumiere: { nom: "Lumière", icon: "☀️", couleur: "bg-yellow-300", textColor: "text-slate-950" },
  dark: { nom: "Ombre", icon: "🌑", couleur: "bg-purple-700", textColor: "text-white" },
};

const ELEMENT_CHAIN = ["eau", "feu", "terre", "air"];

const isStrongAgainst = (atk, def) => {
  if (atk === "lumiere" && def === "dark") return true;
  if (atk === "dark" && def === "lumiere") return true;
  const idx = ELEMENT_CHAIN.indexOf(atk);
  if (idx === -1) return false;
  return ELEMENT_CHAIN[(idx + 1) % ELEMENT_CHAIN.length] === def;
};

// Retourne le multiplicateur élémentaire
// 2 = super-efficace, 0.5 = peu efficace, 1 = neutre
export const elementMultiplier = (atkElem, defElem) => {
  if (!atkElem || !defElem) return 1;
  if (isStrongAgainst(atkElem, defElem)) return 2;
  if (isStrongAgainst(defElem, atkElem)) return 0.5;
  return 1;
};

// Scaling exponentiel des stats ennemies par étage
export const scaleEnemyStats = (baseStats, floor) => {
  const mult = Math.pow(1.42, floor - 1);
  return {
    pv: Math.max(1, Math.round(baseStats.pv * mult)),
    pvMax: Math.max(1, Math.round(baseStats.pv * mult)),
    atk: Math.max(1, Math.round(baseStats.atk * mult)),
    def: Math.max(0, Math.round(baseStats.def * mult)),
    vit: Math.max(1, Math.round(baseStats.vit * (1 + (floor - 1) * 0.15))),
    crit: Math.min(60, Math.round(baseStats.crit + (floor - 1) * 1.5)),
    degatsCrit: Math.round(baseStats.degatsCrit + (floor - 1) * 1),
    degatsElem: Math.max(0, Math.round(baseStats.degatsElem * mult)),
    res: Math.min(80, Math.round(baseStats.res + (floor - 1) * 2)),
    precision: Math.min(99, Math.round(baseStats.precision + (floor - 1) * 1)),
  };
};

// Stats effectives = baseStats + combatBuffs
export const effectiveStats = (baseStats, combatBuffs = {}) => {
  const result = { ...baseStats };
  for (const key of Object.keys(combatBuffs)) {
    result[key] = (result[key] || 0) + (combatBuffs[key] || 0);
  }
  if (combatBuffs.pv) result.pvMax = (result.pvMax || 0) + combatBuffs.pv;
  return result;
};

// Calcul de dégâts générique
// options: { formula, multiplier, guaranteedCrit, ignoreCrit, dccMultiplier, atkElement, defElement }
export const rollPrecision = (attackerStats, defenderStats) => {
  const evasion = (defenderStats?.vit || 0) * 0.5;
  const hitChance = Math.max(15, Math.min(99, (attackerStats?.precision || 80) - evasion));
  return Math.random() * 100 <= hitChance;
};

// Jet de résistance aux effets néfastes :
// la cible résiste avec sa stat RES (en %). Plus la RES est élevée, plus il est dur de poser l'effet.
// Retourne true si l'effet s'applique, false si la cible résiste.
export const rollStatusResist = (defenderStats) => {
  const res = Math.max(0, Math.min(100, defenderStats?.res || 0));
  // proba d'appliquer = (100 - RES) / 100
  return Math.random() * 100 >= res;
};

export const computeDamageWithFormula = (attackerStats, defenderStats, options = {}, extras = {}) => {
  const formula = options.formula || ["atk"];
  const multiplier = options.multiplier || 1;
  const dccMultiplier = options.dccMultiplier || 1;
  const elemMult = elementMultiplier(options.atkElement, options.defElement);

  // Test précision
  const hitRoll = Math.random() * 100;
  const evasion = (defenderStats.vit || 0) * 0.5;
  const hitChance = Math.max(15, Math.min(99, (attackerStats.precision || 80) - evasion));
  if (!options.forceHit && hitRoll > hitChance) {
    return { hit: false, crit: false, damage: 0, elemMult, log: "Esquive !" };
  }

  // Test crit
  const isCrit = !options.ignoreCrit && (options.guaranteedCrit || (Math.random() * 100) < (attackerStats.crit || 0));

  // Construction des dégâts à partir de la formule
  let rawAtk = 0;
  let rawElem = 0;
  for (const term of formula) {
    if (term === "atk") rawAtk += attackerStats.atk || 0;
    else if (term === "def") rawAtk += attackerStats.def || 0;
    else if (term === "vit") rawAtk += attackerStats.vit || 0;
    else if (term === "degatsElem") rawElem += attackerStats.degatsElem || 0;
    else if (term === "muttonAtk") rawAtk += (attackerStats.atk || 0);
    else if (term === "pvBns") rawAtk += extras.pvBns || 0;
    else if (term === "defBns") rawAtk += extras.defBns || 0;
    else if (term === "atkBns") rawAtk += extras.atkBns || 0;
    else if (term === "vitBns") rawAtk += extras.vitBns || 0;
    else if (term === "muttonsConsumed") rawAtk += (extras.muttonsConsumed || 0) * (attackerStats.atk || 0);
    else if (term === "goldPower") rawAtk += extras.goldPower || 0;
  }
  rawAtk *= multiplier;
  rawElem *= multiplier;

  // Multiplicateur élémentaire appliqué aux dégâts élémentaires
  rawElem *= elemMult;

  // Réduction défense
  const defReduction = (defenderStats.def || 0) * 0.5;
  let physical = Math.max(0, rawAtk - defReduction);

  // Bonus crit (DCC en valeur absolue)
  if (isCrit) physical += (attackerStats.degatsCrit || 0) * dccMultiplier;

  // Réduction résistance (en %)
  const elemAfterRes = rawElem * (1 - Math.min(100, defenderStats.res || 0) / 100);

  const total = Math.max(1, Math.round(physical + elemAfterRes));
  return {
    hit: true,
    crit: isCrit,
    damage: total,
    elemMult,
    log: isCrit ? `CRITIQUE ! ${total} dégâts` : `${total} dégâts`,
  };
};

// ===== Bâtisseur : scaling avec l'or amassé dans la run =====
// Bonus d'ATK permanent (passif "Or = Puissance") : +1 ATK par tranche d'or gagné.
export const GOLD_ATK_DIVISOR = 70;
// Bonus de l'ultime "Grand Œuvre" (plus généreux pour un gros pic).
export const GOLD_ULT_DIVISOR = 22;
export const goldAtkBonus = (goldEarned = 0) => Math.floor(Math.max(0, goldEarned) / GOLD_ATK_DIVISOR);
export const goldUltBonus = (goldEarned = 0) => Math.floor(Math.max(0, goldEarned) / GOLD_ULT_DIVISOR);

// ===== Bâtisseur : vol d'or sur les ennemis =====
// À chaque attaque qui touche, le Bâtisseur dérobe de l'or à sa cible.
// Le butin grimpe avec l'étage (danger croissant) et le niveau du joueur.
export const GOLD_STEAL_BASE = 2;
export const GOLD_STEAL_PER_FLOOR = 2;
export const GOLD_STEAL_PER_LEVEL = 0.5;
export const goldStealAmount = (floor = 1, level = 1) =>
  Math.floor(GOLD_STEAL_BASE + GOLD_STEAL_PER_FLOOR * Math.max(1, floor) + GOLD_STEAL_PER_LEVEL * Math.max(1, level));

// Crée un personnage
export const createCharacter = (classeId, CLASSES, heritedPassifs = []) => {
  const classe = CLASSES[classeId];
  const base = { ...classe.baseStats, pvMax: classe.baseStats.pv };
  const character = {
    classeId,
    nom: classe.nom,
    stats: base,
    gold: 50,
    passifs: [],
    energy: ENERGY_INIT,
    energyMax: classe.energyMax || ENERGY_MAX,
    floor: 1,
    runLevel: 1,
    runXp: 0,
    inventory: [],
    purchasedCompetences: [],
    visitedShops: [],
    runStats: {
      totalDamageDealt: 0,
      totalDamageTaken: 0,
      maxHit: 0,
      kills: 0,
      enemiesDefeated: 0,
      bossesDefeated: 0,
      goldEarned: 0,
      goldSpent: 0,
      potionsBought: 0,
      chestsOpened: 0,
      sanctuariesVisited: 0,
      turnsPlayed: 0,
      criticalsLanded: 0,
      damageBySkill: {}, // { "Skill name": damage }
    },
  };
  for (const p of heritedPassifs) {
    character.passifs.push(p);
    applyPassifEffect(character, p);
  }
  return character;
};

const applyPassifEffect = (character, passif) => {
  const stat = passif.stat;
  if (stat === "pv") {
    character.stats.pv += passif.value;
    character.stats.pvMax += passif.value;
  } else if (stat === "heal") {
    character.stats.pv = Math.min(character.stats.pvMax, character.stats.pv + passif.value);
  } else if (stat === "all_flat") {
    for (const s of ["atk", "def", "vit", "pv", "crit", "degatsCrit", "degatsElem", "res", "precision"]) {
      character.stats[s] = (character.stats[s] || 0) + passif.value;
      if (s === "pv") character.stats.pvMax = (character.stats.pvMax || 0) + passif.value;
    }
  } else if (character.stats[stat] !== undefined) {
    character.stats[stat] += passif.value;
  }
};

// Vérifie qu'une éventuelle condition (hp_full / hp_low / no_damage_taken) est satisfaite
const passifConditionSatisfied = (passif, character, ctx = {}) => {
  if (!passif.condition) return true;
  if (passif.condition === "hp_full") return character.stats.pv >= character.stats.pvMax;
  if (passif.condition === "hp_low") return character.stats.pv <= Math.ceil(character.stats.pvMax * 0.3);
  if (passif.condition === "no_damage_taken") return !!ctx.noDamageTaken;
  return true;
};

export const applyPassivesAfterCombat = (character, ctx = {}) => {
  const updated = { ...character, stats: { ...character.stats } };
  for (const passif of character.passifs || []) {
    if (passif.trigger === "combat" && passifConditionSatisfied(passif, updated, ctx)) {
      applyPassifEffect(updated, passif);
    }
  }
  return updated;
};

// Trigger spécifique à un type de salle (elite/boss/chest/sanctuary)
export const applyPassivesAfterRoomDone = (character, roomType, ctx = {}) => {
  if (!roomType) return character;
  const triggerName = `${roomType}_done`;
  const updated = { ...character, stats: { ...character.stats } };
  for (const passif of character.passifs || []) {
    if (passif.trigger === triggerName && passifConditionSatisfied(passif, updated, ctx)) {
      applyPassifEffect(updated, passif);
    }
  }
  return updated;
};

// À chaque nouvel étage franchi (passifs "floor", ex: Croissance, Fortification)
export const applyPassivesOnFloorAdvance = (character) => {
  const updated = { ...character, stats: { ...character.stats } };
  for (const passif of character.passifs || []) {
    if (passif.trigger === "floor") applyPassifEffect(updated, passif);
  }
  return updated;
};

// À chaque salle traversée (toute salle confondue)
export const applyPassivesPerRoom = (character, ctx = {}) => {
  const updated = { ...character, stats: { ...character.stats } };
  for (const passif of character.passifs || []) {
    // Compat : on accepte aussi "tile" pour les save games anciens
    if ((passif.trigger === "room" || passif.trigger === "tile") && passifConditionSatisfied(passif, updated, ctx)) {
      applyPassifEffect(updated, passif);
    }
  }
  return updated;
};

// Alias de compat (anciennes runs / save games avec "tile")
export const applyPassivesPerTile = applyPassivesPerRoom;

export const applyChestBonuses = (character, bonuses) => {
  const updated = { ...character, stats: { ...character.stats } };
  for (const [key, val] of Object.entries(bonuses)) {
    if (key === "pv") {
      updated.stats.pv += val;
      updated.stats.pvMax += val;
    } else {
      updated.stats[key] = (updated.stats[key] || 0) + val;
    }
  }
  return updated;
};

// Status effects helpers
export const newStatusEffects = () => ({
  brulure: null,
  stun: null,
  gel: null,
  garde: null,
  posture: null,
  provoque: null,
  combatBuffs: { atk: 0, def: 0, vit: 0, pv: 0, crit: 0, degatsElem: 0 },
  timedBuffs: [], // bonus de stats TEMPORAIRES : [{ stat, value, duration }] ; pliés dans combatBuffs et retirés à expiration
});

// Tick des status à la fin du tour (DOTs + decrement)
// NOTE: la brûlure ne peut JAMAIS infliger de critique
export const tickStatusEffects = (effects) => {
  const updated = { ...effects, combatBuffs: { ...(effects.combatBuffs || { atk: 0, def: 0, vit: 0, pv: 0, crit: 0, degatsElem: 0 }) } };
  const logs = [];
  let damage = 0;

  if (updated.brulure && updated.brulure.duration > 0) {
    damage += updated.brulure.damage; // pas de crit possible
    logs.push(`🔥 Brûlure : ${updated.brulure.damage} dégâts`);
    updated.brulure = { ...updated.brulure, duration: updated.brulure.duration - 1 };
    if (updated.brulure.duration <= 0) {
      updated.brulure = null;
      logs.push("La brûlure se dissipe.");
    }
  }
  if (updated.stun && updated.stun.duration > 0) {
    updated.stun = { ...updated.stun, duration: updated.stun.duration - 1 };
    if (updated.stun.duration <= 0) updated.stun = null;
  }
  if (updated.gel && updated.gel.duration > 0) {
    updated.gel = { ...updated.gel, duration: updated.gel.duration - 1 };
    if (updated.gel.duration <= 0) {
      updated.gel = null;
      logs.push("Le gel se dissipe.");
    }
  }
  if (updated.garde && updated.garde.duration > 0) {
    updated.garde = { ...updated.garde, duration: updated.garde.duration - 1 };
    if (updated.garde.duration <= 0) updated.garde = null;
  }
  if (updated.posture && updated.posture.duration > 0) {
    // Ne décrémente pas la posture - elle reste active jusqu'à déclenchement auto
  }
  if (updated.provoque && updated.provoque.duration > 0) {
    updated.provoque = { ...updated.provoque, duration: updated.provoque.duration - 1 };
    if (updated.provoque.duration <= 0) updated.provoque = null;
  }

  // Bonus temporaires : décrémente et retire de combatBuffs à expiration
  if (Array.isArray(updated.timedBuffs) && updated.timedBuffs.length) {
    const stillActive = [];
    for (const tb of updated.timedBuffs) {
      const nd = (tb.duration || 0) - 1;
      if (nd > 0) {
        stillActive.push({ ...tb, duration: nd });
      } else {
        updated.combatBuffs[tb.stat] = (updated.combatBuffs[tb.stat] || 0) - (tb.value || 0);
        logs.push(`⏳ Bonus ${(tb.stat || "").toUpperCase()} expiré.`);
      }
    }
    updated.timedBuffs = stillActive;
  }

  return { damage, logs, updatedEffects: updated };
};

// Vérifie si la cible est gelée (= stun via gel)
export const isFrozen = (effects) => effects && effects.gel && effects.gel.duration > 0;

// Exécute une compétence
export const executeCompetence = (competence, playerStats, enemyStats, extras = {}) => {
  const result = { hits: [], logs: [], applyOnSelf: null, applyOnEnemy: null, healFlat: 0, healPercent: 0, healFormula: null, healFromEnemyCrit: false, healFromBuff: null, healFromBuffMult: 1, addMuttons: 0, consumeMuttons: 0, consumeBuffs: null, absorbVitOnKill: false, executeIfVitLower: false, elemMult: 1, enemyStatusLanded: true, enemyStatusResisted: false };

  // Multiplier DCC
  let dccMult = 1;
  if (competence.dccDoubleIfFrozen && isFrozen(extras.enemyEffects)) dccMult = 2;
  if (competence.dccQuadrupleIfFullCrit && playerStats.crit >= 100) dccMult = 4;

  if (competence.type === "attaque") {
    const hit = computeDamageWithFormula(playerStats, enemyStats, {
      formula: competence.formula,
      multiplier: competence.multiplier || 1,
      guaranteedCrit: competence.guaranteedCrit,
      dccMultiplier: dccMult,
      atkElement: extras.atkElement,
      defElement: extras.defElement,
    }, extras);
    result.hits.push(hit);
    result.elemMult = hit.elemMult || 1;
    // Pour une attaque : le statut néfaste atterrit uniquement si l'attaque touche.
    result.enemyStatusLanded = !!hit.hit;
  } else if (competence.applyOnEnemy) {
    // Pour une non-attaque qui applique un effet à l'ennemi : jet de précision dédié.
    result.enemyStatusLanded = rollPrecision(playerStats, enemyStats);
  }

  // 2e jet : résistance aux effets néfastes (RES de la cible)
  // S'applique uniquement aux effets potentiellement "néfastes" sur l'ennemi.
  if (competence.applyOnEnemy && result.enemyStatusLanded) {
    const cfg = competence.applyOnEnemy;
    const isHarmful = !!(cfg.brulure || cfg.gel || cfg.stun || cfg.combatBuff);
    if (isHarmful && !rollStatusResist(enemyStats)) {
      result.enemyStatusResisted = true;
      result.enemyStatusLanded = false;
    }
  }

  if (competence.applyOnEnemy && result.enemyStatusLanded) {
    result.applyOnEnemy = buildStatusFromConfig(competence.applyOnEnemy, playerStats);
  }
  if (competence.applyOnSelf) result.applyOnSelf = buildStatusFromConfig(competence.applyOnSelf, playerStats);

  if (competence.healFlat) result.healFlat = competence.healFlat;
  if (competence.healPercent) result.healPercent = competence.healPercent;
  if (competence.healFormula) result.healFormula = competence.healFormula;
  if (competence.healFromEnemyCrit) result.healFromEnemyCrit = true;
  if (competence.healFromBuff) {
    result.healFromBuff = competence.healFromBuff;
    result.healFromBuffMult = competence.healFromBuffMult || 1;
  }
  if (competence.addMuttons) result.addMuttons = competence.addMuttons;
  if (competence.consumeMuttons) result.consumeMuttons = competence.consumeMuttons;
  if (competence.consumeAllMuttons) result.consumeAllMuttons = true;
  if (competence.consumeBuffs) result.consumeBuffs = competence.consumeBuffs;
  if (competence.absorbVitOnKill) result.absorbVitOnKill = true;
  if (competence.executeIfVitLower) result.executeIfVitLower = true;

  return result;
};

const buildStatusFromConfig = (config, attackerStats) => {
  const status = {};
  if (config.brulure) {
    const dmg = config.brulure.useElem ? (attackerStats.degatsElem || 1) : config.brulure.damage || 1;
    status.brulure = { duration: config.brulure.duration, damage: Math.max(1, dmg) };
  }
  if (config.stun) {
    status.stun = { duration: config.stun.duration || config.stun };
  }
  if (config.gel) {
    status.gel = { duration: config.gel.duration };
  }
  if (config.garde) {
    status.garde = { duration: config.garde.duration, useEnemyElem: config.garde.useEnemyElem, counterBrulure: config.garde.counterBrulure };
  }
  if (config.posture) {
    status.posture = { duration: config.posture.duration, formula: config.posture.formula, stun: config.posture.stun };
  }
  if (config.provoque) {
    status.provoque = { duration: config.provoque.duration };
  }
  if (config.combatBuff) {
    // Peut être un objet ou un array d'objets
    const buffs = Array.isArray(config.combatBuff) ? config.combatBuff : [config.combatBuff];
    status._buffsToApply = buffs;
  }
  return status;
};

export const mergeStatus = (existing, incoming) => {
  if (!incoming) return existing;
  const result = { ...existing };
  if (!result.combatBuffs) result.combatBuffs = { atk: 0, def: 0, vit: 0, pv: 0, crit: 0, degatsElem: 0 };
  else result.combatBuffs = { ...result.combatBuffs };

  for (const [key, value] of Object.entries(incoming)) {
    if (key === "_buffsToApply") continue;
    if (!result[key]) result[key] = value;
    else {
      result[key] = {
        ...result[key],
        ...value,
        duration: Math.max(result[key].duration || 0, value.duration || 0),
        damage: Math.max(result[key].damage || 0, value.damage || 0),
      };
    }
  }
  return result;
};

// Applique les combatBuffs incrémentaux d'un buff config
// buffsToApply: array de { stat, value? , fromStat?, doubleBns? }
// referenceStats: stats joueur courantes (pour fromStat)
// currentCombatBuffs: combatBuffs actuels (pour doubleBns)
export const computeBuffsValues = (buffsToApply, referenceStats, currentCombatBuffs) => {
  const result = {};
  for (const buff of buffsToApply || []) {
    let val = buff.value || 0;
    if (buff.fromStat) val = referenceStats[buff.fromStat] || 0;
    if (buff.doubleBns) val = currentCombatBuffs[buff.doubleBns] || 0;
    result[buff.stat] = (result[buff.stat] || 0) + val;
  }
  return result;
};

// === RUN LEVEL SYSTEM ===
// Niveau 1 au début. Désormais, AUCUNE compétence ne se débloque par niveau
// (toutes les non-base sont à acheter en boutique). Chaque level-up donne +1 à toutes les stats flat.
export const MAX_COMPETENCE_LEVEL = 1;

export const xpForRunLevel = (level) => {
  if (level <= 1) return 0;
  let total = 0;
  for (let n = 2; n <= level; n++) total += 50 + 30 * (n - 2);
  return total;
};

export const runLevelFromXp = (xp) => {
  let level = 1;
  while (xpForRunLevel(level + 1) <= xp) level++;
  return {
    level,
    currentLevelStart: xpForRunLevel(level),
    nextLevelAt: xpForRunLevel(level + 1),
  };
};

const FLAT_STATS_AFTER_MAX = ["atk", "def", "vit", "crit", "degatsCrit", "degatsElem", "res", "precision"];
// PV scale plus fort que les autres stats (utile mais pas écrasant)
const PV_GAIN_PER_LEVEL = 5;
const STAT_GAIN_PER_LEVEL = 1;

export const grantRunXp = (character, amount) => {
  if (!character || !amount || amount <= 0) return { character, levelsGained: 0, newXp: character?.runXp || 0 };
  const oldLevel = character.runLevel || 1;
  const newXp = (character.runXp || 0) + amount;
  const newLevel = runLevelFromXp(newXp).level;
  const updated = { ...character, runXp: newXp, runLevel: newLevel, stats: { ...character.stats } };
  const gained = newLevel - oldLevel;
  if (gained > 0) {
    for (let l = oldLevel + 1; l <= newLevel; l++) {
      // Toutes les stats flat (sauf PV) : +1 par niveau
      for (const s of FLAT_STATS_AFTER_MAX) {
        updated.stats[s] = (updated.stats[s] || 0) + STAT_GAIN_PER_LEVEL;
      }
      // PV : gain plus fort pour rester pertinent en fin de run
      updated.stats.pv = (updated.stats.pv || 0) + PV_GAIN_PER_LEVEL;
      updated.stats.pvMax = (updated.stats.pvMax || 0) + PV_GAIN_PER_LEVEL;
    }
    // Heal 15% PV à chaque level-up pour ne pas mourir à un mauvais moment
    updated.stats.pv = Math.min(updated.stats.pvMax, updated.stats.pv + Math.ceil(updated.stats.pvMax * 0.15));
  }
  return { character: updated, levelsGained: gained, newXp };
};

export const unlockedCompetences = (character, classe) => {
  const level = character?.runLevel || 1;
  const purchased = new Set(character?.purchasedCompetences || []);
  return (classe?.competences || []).filter((c) => {
    if (c.purchasable) return purchased.has(c.id);
    return (c.unlockLevel || 1) <= level;
  });
};

export const isCompetenceUnlocked = (competence, character) => {
  if (competence.purchasable) return (character?.purchasedCompetences || []).includes(competence.id);
  return (competence.unlockLevel || 1) <= (character?.runLevel || 1);
};

// === INVENTORY HELPERS ===
export const addItemToInventory = (character, itemId) => {
  const inv = [...(character.inventory || [])];
  inv.push({ id: itemId, uuid: `${itemId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` });
  return { ...character, inventory: inv };
};

export const removeItemFromInventory = (character, uuid) => {
  return { ...character, inventory: (character.inventory || []).filter((i) => i.uuid !== uuid) };
};

export const applyItemEffectOutOfCombat = (character, item) => {
  if (!item?.effect) return character;
  const eff = item.effect;
  let updated = { ...character, stats: { ...character.stats } };
  if (eff.type === "heal_percent") {
    const heal = Math.ceil(updated.stats.pvMax * eff.value);
    updated.stats.pv = Math.min(updated.stats.pvMax, updated.stats.pv + heal);
  } else if (eff.type === "permanent_stat") {
    updated.stats[eff.stat] = (updated.stats[eff.stat] || 0) + eff.value;
    if (eff.stat === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + eff.value;
  } else if (eff.type === "multi_stat") {
    for (const [stat, val] of Object.entries(eff.stats || {})) {
      updated.stats[stat] = (updated.stats[stat] || 0) + val;
      if (stat === "pv") updated.stats.pvMax = Math.max(1, (updated.stats.pvMax || 0) + val);
    }
    // Clamp pv to pvMax
    if (updated.stats.pv > updated.stats.pvMax) updated.stats.pv = updated.stats.pvMax;
  } else if (eff.type === "all_flat_stats") {
    const FLATS = ["atk", "def", "vit", "pv", "crit", "degatsCrit", "degatsElem", "res", "precision"];
    for (const s of FLATS) {
      updated.stats[s] = (updated.stats[s] || 0) + eff.value;
      if (s === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + eff.value;
    }
  } else if (eff.type === "percent_all_stats") {
    const FLATS = ["atk", "def", "vit", "pv", "crit", "degatsCrit", "degatsElem", "res", "precision"];
    for (const s of FLATS) {
      const add = Math.round((updated.stats[s] || 0) * eff.value);
      updated.stats[s] = (updated.stats[s] || 0) + add;
      if (s === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + add;
    }
  } else if (eff.type === "percent_stat") {
    const add = Math.round((updated.stats[eff.stat] || 0) * eff.value);
    updated.stats[eff.stat] = (updated.stats[eff.stat] || 0) + add;
    if (eff.stat === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + add;
  } else if (eff.type === "revive_token") {
    updated.reviveTokens = (updated.reviveTokens || 0) + 1;
  } else if (eff.type === "run_xp") {
    const { character: c2 } = grantRunXp(updated, eff.value);
    updated = c2;
  }
  return updated;
};

